import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import {
  useDriversStore,
  type AvailableDriver,
  type Driver,
} from "../../store/admin/driversStore";

export interface DriverListParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

export interface DriverListResult {
  data: Driver[];
  meta?: ListMeta;
}

export function useDriversList(params: DriverListParams = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.drivers.list(companyId, params),
    queryFn: ({ signal }) =>
      getJson<DriverListResult>(
        "/driverList",
        { page: 1, limit: 6, ...params },
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}

function extractDrivers(body: unknown): AvailableDriver[] {
  const payload = (body as { data?: unknown })?.data ?? body;
  if (Array.isArray(payload)) return payload as AvailableDriver[];
  const nested = (payload as { data?: unknown })?.data;
  if (Array.isArray(nested)) return nested as AvailableDriver[];
  return [];
}

export function useAvailableDrivers() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const selectedCompany = useVehiclesSocketStore((s) => s.selectedCompany);
  const cid = selectedCompany || companyId;

  const query = useQuery({
    queryKey: qk.availableDrivers(cid),
    queryFn: ({ signal }) =>
      getJson<unknown>(
        "/availableDrivers",
        { company_id: cid ?? "" },
        { signal },
        { headers: { "x-company-id": cid ?? "" } },
      ).then(extractDrivers),
    enabled: !!cid,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data) {
      useDriversStore.setState({ availableDrivers: query.data });
    }
  }, [query.data]);

  return query;
}
